/**
 * Pure geographic bbox helpers for viewport preload (Web Mercator approximation, no domain logic).
 */

import type { FeatureCollection } from "geojson";
import type { LngLatBounds } from "maplibre-gl";

import type { BboxPreloadConfig, MapBbox, MapViewportContext } from "./bboxPreload.types";

/** MapLibre default vector tile size used by this project. */
const MAP_TILE_SIZE_PX = 512;

/** Maximum latitude for Web Mercator clamping. */
const MAX_MERCATOR_LAT = 85;

/**
 * Returns degrees of longitude per pixel at the given zoom (equator reference).
 *
 * @remarks Approximation: `360 / (tileSize * 2^zoom)`. Matches MapLibre world size at zoom z.
 * @param zoom Decimal map zoom.
 * @returns Longitude degrees spanned by one screen pixel at the equator.
 */
export const degreesPerPixelAtZoom = ( zoom: number ): number => {
    return 360 / ( MAP_TILE_SIZE_PX * Math.pow( 2, zoom ) );
};

/**
 * Clamps a latitude to the Web Mercator valid range.
 *
 * @param latitude Latitude in degrees.
 * @returns Clamped latitude.
 */
const clampLatitude = ( latitude: number ): number => {
    return Math.max( -MAX_MERCATOR_LAT, Math.min( MAX_MERCATOR_LAT, latitude ) );
};

/**
 * Clamps a longitude to [-180, 180].
 *
 * @param longitude Longitude in degrees.
 * @returns Clamped longitude.
 */
const clampLongitude = ( longitude: number ): number => {
    if ( longitude < -180 ) {
        return -180;
    }

    if ( longitude > 180 ) {
        return 180;
    }

    return longitude;
};

/**
 * Converts MapLibre bounds to {@link MapBbox}.
 *
 * @param bounds MapLibre lng/lat bounds from `map.getBounds()`.
 * @returns Generic map preload bbox.
 */
export const mapBoundsToBbox = ( bounds: LngLatBounds ): MapBbox => ( {
    "minLat": bounds.getSouth(),
    "minLon": bounds.getWest(),
    "maxLat": bounds.getNorth(),
    "maxLon": bounds.getEast(),
} );

/**
 * Computes a viewport bbox as if the map were centered at `center` with given zoom and pixel size.
 *
 * @remarks Uses Web Mercator degrees-per-pixel with `cos(lat)` latitude correction for half-height.
 * @param centerLat Center latitude.
 * @param centerLng Center longitude.
 * @param zoom Reference zoom.
 * @param viewportWidthPx Container width in pixels.
 * @param viewportHeightPx Container height in pixels.
 * @returns Geographic bbox enclosing that virtual viewport.
 */
export const computeViewportBboxAtZoom = (
    centerLat: number,
    centerLng: number,
    zoom: number,
    viewportWidthPx: number,
    viewportHeightPx: number,
): MapBbox => {
    const degreesPerPixel = degreesPerPixelAtZoom( zoom );
    const latitudeRadians = centerLat * ( Math.PI / 180 );
    const cosineLatitude  = Math.max( Math.cos( latitudeRadians ), 1e-6 );

    const halfLng = ( viewportWidthPx / 2 ) * degreesPerPixel;
    const halfLat = ( viewportHeightPx / 2 ) * degreesPerPixel / cosineLatitude;

    return {
        "minLat": clampLatitude( centerLat - halfLat ),
        "minLon": clampLongitude( centerLng - halfLng ),
        "maxLat": clampLatitude( centerLat + halfLat ),
        "maxLon": clampLongitude( centerLng + halfLng ),
    };
};

/**
 * Returns the center of a bbox.
 *
 * @param bbox Input bbox.
 * @returns Center lat/lng.
 */
const bboxCenter = ( bbox: MapBbox ): {
    "lat": number;
    "lng": number;
} => ( {
    "lat": ( bbox.minLat + bbox.maxLat ) / 2,
    "lng": ( bbox.minLon + bbox.maxLon ) / 2,
} );

/**
 * Expands a bbox by scaling each half-axis from its center (linear multiplier on spans).
 *
 * @param bbox Source bbox.
 * @param linearMultiplier Factor applied to each half-axis (e.g. 4 → 4× width and height).
 * @returns Expanded bbox.
 */
export const expandBboxFromCenter = ( bbox: MapBbox, linearMultiplier: number ): MapBbox => {
    const center  = bboxCenter( bbox );
    const halfLat = ( bbox.maxLat - bbox.minLat ) / 2 * linearMultiplier;
    const halfLng = ( bbox.maxLon - bbox.minLon ) / 2 * linearMultiplier;

    return {
        "minLat": clampLatitude( center.lat - halfLat ),
        "minLon": clampLongitude( center.lng - halfLng ),
        "maxLat": clampLatitude( center.lat + halfLat ),
        "maxLon": clampLongitude( center.lng + halfLng ),
    };
};

/**
 * Shrinks a bbox by scaling each half-axis toward its center (comfort / prefetch trigger zone).
 *
 * @param bbox Source bbox (typically last preloaded extent).
 * @param shrinkFactor Factor in (0, 1] applied to each half-axis (e.g. 0.85).
 * @returns Inner bbox; when the visible viewport exits this inner box, prefetch is triggered.
 */
export const shrinkBboxFromCenter = ( bbox: MapBbox, shrinkFactor: number ): MapBbox => {
    const center  = bboxCenter( bbox );
    const halfLat = ( bbox.maxLat - bbox.minLat ) / 2 * shrinkFactor;
    const halfLng = ( bbox.maxLon - bbox.minLon ) / 2 * shrinkFactor;

    return {
        "minLat": clampLatitude( center.lat - halfLat ),
        "minLon": clampLongitude( center.lng - halfLng ),
        "maxLat": clampLatitude( center.lat + halfLat ),
        "maxLon": clampLongitude( center.lng + halfLng ),
    };
};

/**
 * Returns the smallest axis-aligned bbox that contains both inputs.
 *
 * @param first First bbox.
 * @param second Second bbox.
 * @returns Union bbox in WGS84.
 */
export const unionBboxes = ( first: MapBbox, second: MapBbox ): MapBbox => ( {
    "minLat": Math.min( first.minLat, second.minLat ),
    "minLon": Math.min( first.minLon, second.minLon ),
    "maxLat": Math.max( first.maxLat, second.maxLat ),
    "maxLon": Math.max( first.maxLon, second.maxLon ),
} );

/**
 * Tests whether `inner` is fully contained in `outer` (axis-aligned bbox).
 *
 * @param inner Inner bbox (e.g. visible viewport).
 * @param outer Outer bbox (e.g. preloaded or inner comfort zone).
 * @returns True when every corner of `inner` lies inside `outer`.
 */
export const bboxContains = ( inner: MapBbox, outer: MapBbox ): boolean => {
    return inner.minLat >= outer.minLat &&
      inner.maxLat <= outer.maxLat &&
      inner.minLon >= outer.minLon &&
      inner.maxLon <= outer.maxLon;
};

/**
 * Builds a closed Polygon GeoJSON feature collection from a preload bbox.
 *
 * @param bbox Geographic bounds.
 * @returns FeatureCollection with one polygon feature.
 */
export const bboxToPolygonFeatureCollection = ( bbox: MapBbox ): FeatureCollection => ( {
    "type":     "FeatureCollection",
    "features": [
        {
            "type":       "Feature",
            "properties": {},
            "geometry":   {
                "type":        "Polygon",
                "coordinates": [
                    [
                        [ bbox.minLon, bbox.minLat ],
                        [ bbox.maxLon, bbox.minLat ],
                        [ bbox.maxLon, bbox.maxLat ],
                        [ bbox.minLon, bbox.maxLat ],
                        [ bbox.minLon, bbox.minLat ],
                    ],
                ],
            },
        },
    ],
} );

/**
 * Linear scale applied to {@link MapViewportContext.visibleBbox} half-axes for prefetch.
 *
 * @remarks Matches legacy intent: `linearExtentMultiplier` at reference zoom, multiplied by
 * `2^(currentZoom - referenceZoom)` when zoomed in so the fetch area grows like the reference
 * viewport without recomputing from pixel width/height (which skews aspect ratio at mid-latitudes).
 * @param context Viewport snapshot from the map controller.
 * @param config Preload configuration.
 * @returns Multiplier passed to {@link expandBboxFromCenter}.
 */
export const computePrefetchScaleFromContext = (
    context: MapViewportContext,
    config: BboxPreloadConfig,
): number => {
    const zoomAboveReference = Math.max(
        0,
        context.currentZoom - config.referenceZoom,
    );

    return config.linearExtentMultiplier * Math.pow( 2, zoomAboveReference );
};

/**
 * Computes the prefetch bbox from MapLibre visible bounds and zoom-dependent scale.
 *
 * @remarks Uses {@link MapViewportContext.visibleBbox} from `map.getBounds()` so the
 * geographic aspect ratio matches the on-screen viewport (landscape stays landscape).
 * @param context Viewport snapshot from the map controller.
 * @param config Preload configuration.
 * @returns Bbox sent to the fetch implementation on the next load.
 */
export const computePrefetchBboxFromContext = (
    context: MapViewportContext,
    config: BboxPreloadConfig,
): MapBbox => {
    const scale = computePrefetchScaleFromContext( context, config );

    return expandBboxFromCenter( context.visibleBbox, scale );
};
